/**
 * Narrator voices (EPIC-0043)
 *
 * One catalog, shared by the API (which resolves an id to a provider call) and
 * the web client (which shows the picker and persists the chosen id). The app
 * never stores a provider voice name directly — it stores a `NarratorVoice.id`,
 * so a voice can be re-pointed at a different provider without invalidating
 * every player's saved preference or the synthesis cache.
 *
 * WHY SERVER-SIDE (ISS-0140, measured 2026-08-23)
 *
 * The original spike synthesised in the browser with Kokoro-82M. Measured at
 * 0.37x realtime on WASM and 0.73x on WebGPU, so a 40-second narration took
 * 54-108 seconds to produce — the free tier as designed was unusable rather
 * than cheap. The same model server-side returns a complete 674-character turn
 * in 922ms at $0.004 per 1k characters, which is about 11 cents for a two-hour
 * solo session: less than a single LLM GM turn.
 *
 * That also killed the "chunk the narration to cut time-to-first-audio" design.
 * Kokoro runs at a flat ~1.0-1.1x realtime from 4 characters to 242, so there
 * is no per-inference overhead for chunking to amortise, and Together bills per
 * character, so three chunks cost exactly what one call costs. Nothing can beat
 * having the whole narration buffered before the first syllable would have
 * played, so synthesis is one call for the whole turn.
 */
/**
 * Free voices — Together's hosted Kokoro-82M. $0.004/1k chars, 24kHz.
 *
 * Doomsworn's register is brass, salt, ash and paperwork, so the default wants
 * to be dry and unhurried; a theatrical narrator fights the tone contract in
 * DOOMSWORN_DM_GUIDANCE.
 */
const KOKORO_VOICES = [
    {
        id: 'narrator-george',
        label: 'George',
        description: 'Dry, unhurried, British. The default Doomsworn narrator.',
        tier: 'free',
        provider: 'together-kokoro',
        model: 'hexgrad/Kokoro-82M',
        voice: 'bm_george',
        fallbackOpenAIVoice: 'onyx',
    },
    {
        id: 'narrator-lewis',
        label: 'Lewis',
        description: 'Lower and slower. Leans grim.',
        tier: 'free',
        provider: 'together-kokoro',
        model: 'hexgrad/Kokoro-82M',
        voice: 'bm_lewis',
        fallbackOpenAIVoice: 'echo',
    },
    {
        id: 'narrator-emma',
        label: 'Emma',
        description: 'Measured, British, even-tempered.',
        tier: 'free',
        provider: 'together-kokoro',
        model: 'hexgrad/Kokoro-82M',
        voice: 'bf_emma',
        fallbackOpenAIVoice: 'nova',
    },
    {
        id: 'narrator-michael',
        label: 'Michael',
        description: 'Warmer American read. Good for lighter tables.',
        tier: 'free',
        provider: 'together-kokoro',
        model: 'hexgrad/Kokoro-82M',
        voice: 'am_michael',
        fallbackOpenAIVoice: 'alloy',
    },
];
/**
 * Paid voices — Cartesia Sonic-2, reached through the existing Together key.
 *
 * 44.1kHz against Kokoro's 24kHz, and audibly less like the house voice of
 * every automated YouTube channel. It costs $0.065/1k chars — roughly $1.75 for
 * a two-hour session, about 16x Kokoro — which is the real marginal cost the
 * paid tier is charging for, and the reason premium voice should be metered
 * rather than flat-rate unlimited.
 *
 * UNAUDITIONED: these three were shortlisted on paper against the Doomsworn
 * register and have not been listened to. Picking the real Narrator is a
 * judgement no benchmark makes — see ISS-0140.
 */
const CARTESIA_VOICES = [
    {
        id: 'premium-nonfiction',
        label: 'Nonfiction Man',
        description: 'Documentary read. States terrible things plainly.',
        tier: 'paid',
        provider: 'together-cartesia',
        model: 'cartesia/sonic-2',
        voice: 'nonfiction man',
        fallbackOpenAIVoice: 'onyx',
    },
    {
        id: 'premium-british-narration',
        label: 'British Narration',
        description: 'Audiobook-grade, unhurried, faintly weary.',
        tier: 'paid',
        provider: 'together-cartesia',
        model: 'cartesia/sonic-2',
        voice: 'british narration lady',
        fallbackOpenAIVoice: 'shimmer',
    },
    {
        id: 'premium-reading-man',
        label: 'Reading Man',
        description: 'Close, low, conversational. Sounds like a GM, not a trailer.',
        tier: 'paid',
        provider: 'together-cartesia',
        model: 'cartesia/sonic-2',
        voice: 'reading man',
        fallbackOpenAIVoice: 'echo',
    },
];
export const NARRATOR_VOICES = [...KOKORO_VOICES, ...CARTESIA_VOICES];
/** The voice a player gets before they ever open the picker. */
export const DEFAULT_NARRATOR_VOICE_ID = 'narrator-george';
export function getNarratorVoice(id) {
    return NARRATOR_VOICES.find((v) => v.id === id);
}
/**
 * Resolve a stored id to a usable voice.
 *
 * Falls back to the default rather than throwing, because a stale id in a
 * player's persisted settings (a voice we retired) must not break narration.
 */
export function resolveNarratorVoice(id) {
    return ((id ? getNarratorVoice(id) : undefined) ??
        getNarratorVoice(DEFAULT_NARRATOR_VOICE_ID));
}
/** Longest narration we will synthesise in one call. */
export const VOICE_MAX_INPUT_CHARS = 4000;
//# sourceMappingURL=voice-types.js.map